import { NextResponse } from "next/server";
import { prisma } from "@/lib/db";
import { calculatePCorrectFromLogs } from "@/lib/scheduler/predictor";
import { getBoxStatus, getUserPhase } from "@/lib/scheduler/predictor";
import {
  DECAY_LAMBDA,
  FOCUS_SET_SIZE,
  NEW_ITEM_P_CORRECT,
  RETRY_DELAY_PROBLEMS,
  STABLE_CORRECT_COUNT,
  STABLE_SESSION_COUNT,
  GRADUATION_THRESHOLD,
  PHASE_1_TOTAL_PROBLEMS,
  LEITNER_INTERVALS,
  BOX_GRADUATED,
} from "@/lib/scheduler/constants";

export async function POST(request: Request) {
  try {
    const body = await request.json();
    const { userId } = body;

    if (!userId) {
      return NextResponse.json({ error: "userId required" }, { status: 400 });
    }

    const now = new Date();

    // Get user phase info
    const { currentPhase, totalSeen } = await getUserPhase(userId);
    const boxStatus = await getBoxStatus(userId);

    // Get all problems with their states
    const problems = await prisma.problem.findMany({
      include: {
        group: true,
        userProblemStates: {
          where: { userId },
        },
        reviewLogs: {
          where: { userId },
          orderBy: { timestamp: "desc" },
          take: 10,
        },
      },
      orderBy: [
        { group: { sequence: "asc" } },
        { sequenceInGroup: "asc" },
      ],
    });

    // Calculate p_correct for each problem and build debug info
    const problemsDebug = problems.map((p) => {
      const state = p.userProblemStates[0];
      const logs = p.reviewLogs;

      const pCorrect = calculatePCorrectFromLogs(
        logs.map((l) => ({ timestamp: l.timestamp, wasCorrect: l.wasCorrect })),
        now
      );

      // Calculate hours since last attempt
      const lastLog = logs[0];
      const hoursSinceLast = lastLog
        ? (now.getTime() - lastLog.timestamp.getTime()) / (1000 * 60 * 60)
        : null;

      // Box info
      const box = state?.box ?? -1;  // -1 means unseen
      const correctCount = state?.correctCount ?? 0;
      const sessionsCorrect = state?.sessionsCorrect ?? "";

      return {
        id: p.id,
        problemText: p.problemText,
        answer: p.answer,
        groupName: p.group.name,
        sequenceInGroup: p.sequenceInGroup,
        // Legacy state (kept for compatibility)
        set: state?.set ?? "new",
        lastResult: state?.lastResult ?? null,
        lastAnsweredAt: state?.lastAnsweredAt ?? null,
        // New box system
        box,
        boxCorrectCount: correctCount,
        sessionsCorrect,
        dueAt: state?.dueAt ?? null,
        // Computed
        pCorrect: Math.round(pCorrect * 100) / 100,
        attemptCount: logs.length,
        correctCount: logs.filter((l) => l.wasCorrect).length,
        hoursSinceLast: hoursSinceLast ? Math.round(hoursSinceLast * 10) / 10 : null,
        // Recent history (last 5)
        recentHistory: logs.slice(0, 5).map((l) => ({
          wasCorrect: l.wasCorrect,
          userInput: l.userInput,
          timestamp: l.timestamp,
          presentedType: l.presentedType,
        })),
      };
    });

    // Get session stats
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    const [todayLogs, totalLogs, recentSessions] = await Promise.all([
      prisma.reviewLog.findMany({
        where: {
          userId,
          timestamp: { gte: todayStart },
        },
        orderBy: { timestamp: "desc" },
      }),
      prisma.reviewLog.count({ where: { userId } }),
      prisma.studySession.findMany({
        where: { userId },
        orderBy: { startedAt: "desc" },
        take: 5,
      }),
    ]);

    // Count by box (new system)
    const boxCounts = {
      unseen: problemsDebug.filter((p) => p.box === -1).length,
      box0: problemsDebug.filter((p) => p.box === 0).length,
      box1: problemsDebug.filter((p) => p.box === 1).length,
      box2: problemsDebug.filter((p) => p.box === 2).length,
      box3: problemsDebug.filter((p) => p.box === 3).length,
      graduated: problemsDebug.filter((p) => p.box === BOX_GRADUATED).length,
    };

    // Legacy set counts (for backward compatibility)
    const setCounts = {
      new: problemsDebug.filter((p) => p.set === "new").length,
      focus: problemsDebug.filter((p) => p.set === "focus").length,
      old: problemsDebug.filter((p) => p.set === "old").length,
    };

    // Focus problems = boxes 0-3
    const focusProblems = problemsDebug.filter((p) => p.box >= 0 && p.box <= 3);
    const focusCorrectCount = focusProblems.filter((p) => p.box === 3).length;
    const masteryPercent = focusProblems.length > 0
      ? Math.round((focusCorrectCount / focusProblems.length) * 100)
      : 0;

    // p_correct distribution for focus set
    const focusPCorrectBuckets = {
      hard: focusProblems.filter((p) => p.pCorrect < 0.5).length,
      medium: focusProblems.filter((p) => p.pCorrect >= 0.5 && p.pCorrect < 0.8).length,
      easy: focusProblems.filter((p) => p.pCorrect >= 0.8).length,
    };

    return NextResponse.json({
      // Phase info (new)
      phase: {
        currentPhase,
        totalSeen,
        totalProblems: problems.length,
        phaseTransitionAt: PHASE_1_TOTAL_PROBLEMS,
      },
      // Box status (new)
      boxStatus,
      boxCounts,
      // Constants
      constants: {
        DECAY_LAMBDA,
        FOCUS_SET_SIZE,
        STABLE_CORRECT_COUNT,
        STABLE_SESSION_COUNT,
        GRADUATION_THRESHOLD,
        PHASE_1_TOTAL_PROBLEMS,
        LEITNER_INTERVALS,
        NEW_ITEM_P_CORRECT,
        RETRY_DELAY_PROBLEMS,
      },
      // Legacy set counts (for backward compatibility)
      setCounts,
      focusCorrectCount,
      masteryPercent,
      focusPCorrectBuckets,
      // Today's activity
      todayStats: {
        total: todayLogs.length,
        correct: todayLogs.filter((l) => l.wasCorrect).length,
        accuracy: todayLogs.length > 0
          ? Math.round((todayLogs.filter((l) => l.wasCorrect).length / todayLogs.length) * 100)
          : 0,
      },
      // All-time stats
      allTimeStats: {
        totalAttempts: totalLogs,
      },
      // Recent sessions
      recentSessions: recentSessions.map((s) => ({
        id: s.id,
        mode: s.mode,
        startedAt: s.startedAt,
        endedAt: s.endedAt,
        completedCount: s.completedCount,
        correctCount: s.correctCount,
      })),
      // All problems with debug info
      problems: problemsDebug,
      // Focus set (boxes 0-3)
      focusSet: problemsDebug.filter((p) => p.box >= 0 && p.box <= 3),
      // Graduated set (box 99)
      oldSet: problemsDebug.filter((p) => p.box === BOX_GRADUATED),
    });
  } catch (error) {
    console.error("Debug API error:", error);
    return NextResponse.json(
      { error: "Internal server error" },
      { status: 500 }
    );
  }
}
